import mixpanel from 'mixpanel-browser';
import { supabase } from '@/integrations/supabase/client';
import { getUserProfile, getUserPlan } from '@/services/user';
import { User } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';
import { AppTrackingService } from './app-tracking-transparency';

interface MixpanelUserProperties {
  $name?: string;
  $email: string;
  username?: string;
  display_name?: string;
  
  // Phase 1: Core properties
  user_role: string;
  account_balance: number;
  registration_date: string;
  days_since_signup: number;
  
  // Subscription data
  subscription_status: 'active' | 'expired' | 'free';
  plan_name: string;
  plan_interval?: string;
  is_subscriber: boolean;
  
  // Reading metrics (basic)
  total_chapters_read?: number;
  total_stories_started?: number;
  last_activity_date?: string;
}

class AnalyticsService {
  private isInitialized = false;
  private hasTrackingPermission = false;

  /**
   * Initialize with ATT permission check
   */
  async init() {
    if (this.isInitialized) return;
    
    try {
      // Wait for ATT permission
      this.hasTrackingPermission = await AppTrackingService.init();
      
      console.log('✅ Analytics initialized with tracking permission:', this.hasTrackingPermission);
      
      if (typeof window !== 'undefined' && mixpanel) {
        this.isInitialized = true;
      }
    } catch (error) {
      console.error('❌ Analytics service initialization failed:', error);
    }
  }

  /**
   * Track Install event (first app open) - ONLY for new users
   */
  async trackInstall() {
    if (!this.isInitialized) {
      console.warn('Analytics not initialized, skipping install tracking');
      return;
    }

    if (!this.hasTrackingPermission) {
      console.log('No tracking permission, skipping install tracking');
      return;
    }

    try {
      // Check if we've already tracked install
      const hasTrackedInstall = await this.hasTrackedInstall();
      
      if (hasTrackedInstall) {
        console.log('Install already tracked, skipping');
        return;
      }

      // 🎯 NEW: Only track install for users created after Mixpanel implementation
      const isNewUser = await this.isNewUserForTracking();
      
      if (!isNewUser) {
        console.log('User created before Mixpanel implementation, skipping install tracking');
        // Mark as tracked to avoid checking again
        await this.markInstallAsTracked();
        return;
      }

      // Get platform info
      const platform = Capacitor.getPlatform();
      const installSource = this.getInstallSource(platform);
      
      // Track install event
      this.track('Install', {
        platform: platform,
        install_source: installSource,
        is_first_open: true,
        app_version: '1.0.0', // TODO: get from config
        timestamp: new Date().toISOString()
      });

      // Mark as tracked
      await this.markInstallAsTracked();
      
      console.log('✅ Install event tracked successfully for new user');
    } catch (error) {
      console.error('❌ Error tracking install event:', error);
    }
  }

  /**
   * Check if user is "new" enough to track install
   * Only track install for users created after we implemented Mixpanel
   */
  private async isNewUserForTracking(): Promise<boolean> {
    try {
      // 🎯 Cutoff date: when we implemented Mixpanel tracking (set to today)
      const MIXPANEL_IMPLEMENTATION_DATE = '2025-01-15T00:00:00.000Z'; // Users created before this date won't get Install events
      
      // Get current user
      const { data: userData } = await supabase.auth.getUser();
      
      if (!userData?.user?.created_at) {
        console.log('No user or creation date found');
        return false;
      }
      
      const userCreatedAt = new Date(userData.user.created_at);
      const cutoffDate = new Date(MIXPANEL_IMPLEMENTATION_DATE);
      
      const isNewUser = userCreatedAt > cutoffDate;
      
      console.log('User creation check:', {
        userCreatedAt: userData.user.created_at,
        cutoffDate: MIXPANEL_IMPLEMENTATION_DATE,
        isNewUser
      });
      
      return isNewUser;
    } catch (error) {
      console.error('Error checking if user is new:', error);
      return false;
    }
  }

  /**
   * Check if install has been tracked
   */
  private async hasTrackedInstall(): Promise<boolean> {
    try {
      // For now, use localStorage for all platforms
      // Can be enhanced later with native storage
      return localStorage.getItem('mixpanel_install_tracked') === 'true';
    } catch (error) {
      console.error('Error checking install tracking status:', error);
      return false;
    }
  }

  /**
   * Mark install as tracked
   */
  private async markInstallAsTracked(): Promise<void> {
    try {
      // For now, use localStorage for all platforms
      // Can be enhanced later with native storage
      localStorage.setItem('mixpanel_install_tracked', 'true');
    } catch (error) {
      console.error('Error marking install as tracked:', error);
    }
  }

  /**
   * Get install source based on platform
   */
  private getInstallSource(platform: string): string {
    switch (platform) {
      case 'ios':
        return 'app_store';
      case 'android':
        return 'google_play';
      case 'web':
        return 'web_app';
      default:
        return 'unknown';
    }
  }

  /**
   * Identify user and set properties (Phase 1)
   */
  async identifyUser(user: User) {
    if (!this.isInitialized || !user?.id) {
      console.warn('Analytics not initialized or user not provided');
      return;
    }

    if (!this.hasTrackingPermission) {
      console.log('No tracking permission, skipping user identification');
      return;
    }

    try {
      console.log('🔍 Identifying user for Mixpanel:', user.id);

      // Step 1: Identify the user
      mixpanel.identify(user.id);

      // Step 2: Get user profile data
      const { profile } = await getUserProfile(user.id);
      const { plan } = await getUserPlan(user.id);

      // Step 3: Get reading metrics
      const readingMetrics = await this.getReadingMetrics(user.id);

      // Step 4: Calculate derived properties
      const userProperties = await this.buildUserProperties(user, profile, plan, readingMetrics);

      // Step 5: Set user properties
      mixpanel.people.set(userProperties);

      console.log('✅ User identified in Mixpanel:', userProperties);
    } catch (error) {
      console.error('❌ Error identifying user in Mixpanel:', error);
    }
  }

  /**
   * Update user properties (call when profile changes)
   */
  async updateUserProperties(user: User) {
    if (!this.isInitialized || !user?.id) return;

    if (!this.hasTrackingPermission) {
      console.log('No tracking permission, skipping user properties update');
      return;
    }

    try {
      const { profile } = await getUserProfile(user.id);
      const { plan } = await getUserPlan(user.id);
      const readingMetrics = await this.getReadingMetrics(user.id);
      
      const userProperties = await this.buildUserProperties(user, profile, plan, readingMetrics);
      
      mixpanel.people.set(userProperties);
      console.log('✅ User properties updated in Mixpanel');
    } catch (error) {
      console.error('❌ Error updating user properties:', error);
    }
  }

  /**
   * Track custom events
   */
  track(eventName: string, properties?: Record<string, any>) {
    if (!this.isInitialized) {
      console.warn('Analytics not initialized, skipping tracking');
      return;
    }

    if (!this.hasTrackingPermission) {
      console.log('No tracking permission, skipping event:', eventName);
      return;
    }

    try {
      console.log('📊 Tracking event:', eventName, properties);
      mixpanel.track(eventName, {
        timestamp: new Date().toISOString(),
        ...properties
      });
    } catch (error) {
      console.error('❌ Error tracking event:', eventName, error);
    }
  }

  /**
   * Get basic reading metrics from database
   */
  private async getReadingMetrics(userId: string) {
    try {
      // Get total chapters read
      const { data: chapterSessions, error: sessionsError } = await supabase
        .from('chapter_sessions')
        .select('chapter_id, percent_read')
        .eq('user_id', userId)
        .gte('percent_read', 85); // Consider 85%+ as "read"

      const totalChaptersRead = chapterSessions?.length || 0;

      // Get unique stories started
      const { data: storySessions, error: storyError } = await supabase
        .from('chapter_sessions')
        .select('chapter_id, chapters(story_id)')
        .eq('user_id', userId)
        .gt('percent_read', 0);

      const uniqueStoryIds = new Set(
        storySessions?.map(session => {
          const chapters = session.chapters as any;
          return Array.isArray(chapters) ? chapters[0]?.story_id : chapters?.story_id;
        }).filter(Boolean) || []
      );
      const totalStoriesStarted = uniqueStoryIds.size;

      // Get last activity
      const { data: lastActivity, error: activityError } = await supabase
        .from('chapter_sessions')
        .select('updated_at')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .limit(1);

      const lastActivityDate = lastActivity?.[0]?.updated_at || null;

      return {
        totalChaptersRead,
        totalStoriesStarted,
        lastActivityDate
      };
    } catch (error) {
      console.error('Error fetching reading metrics:', error);
      return {
        totalChaptersRead: 0,
        totalStoriesStarted: 0,
        lastActivityDate: null
      };
    }
  }

  /**
   * Build user properties object for Mixpanel
   */
  private async buildUserProperties(
    user: User, 
    profile: any, 
    plan: any, 
    readingMetrics: any
  ): Promise<MixpanelUserProperties> {
    // Calculate days since signup
    const signupDate = new Date(user.created_at);
    const daysSinceSignup = Math.floor((Date.now() - signupDate.getTime()) / (1000 * 60 * 60 * 24));

    // Determine subscription status
    let subscriptionStatus: 'active' | 'expired' | 'free' = 'free';
    if (plan?.plan_name && plan.plan_name !== 'Gratuito') {
      // Check if subscription is active (you might need to add more logic here)
      const { data: activeSubs } = await supabase
        .from('web_sales')
        .select('status, current_period_end')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .gt('current_period_end', new Date().toISOString())
        .limit(1);

      subscriptionStatus = activeSubs && activeSubs.length > 0 ? 'active' : 'expired';
    }

    return {
      // Basic info
      $email: user.email || '',
      $name: profile?.display_name || profile?.username || user.email?.split('@')[0] || '',
      username: profile?.username || '',
      display_name: profile?.display_name || '',
      
      // Core properties
      user_role: profile?.role || 'user',
      account_balance: profile?.balance || 0,
      registration_date: signupDate.toISOString().split('T')[0],
      days_since_signup: daysSinceSignup,
      
      // Subscription data
      subscription_status: subscriptionStatus,
      plan_name: plan?.plan_name || 'Gratuito',
      plan_interval: plan?.plan_interval || undefined,
      is_subscriber: subscriptionStatus === 'active',
      
      // Reading metrics
      total_chapters_read: readingMetrics.totalChaptersRead,
      total_stories_started: readingMetrics.totalStoriesStarted,
      last_activity_date: readingMetrics.lastActivityDate
    };
  }

  /**
   * Clear user data (on logout)
   */
  reset() {
    if (!this.isInitialized) return;

    try {
      mixpanel.reset();
      console.log('✅ Mixpanel user data cleared');
    } catch (error) {
      console.error('❌ Error clearing Mixpanel data:', error);
    }
  }
}

// Export singleton instance
export const analytics = new AnalyticsService(); 